import React from 'react';
import {useNavigate} from 'react-router-dom';
import {getFormRequestStatusInfo} from '../common/common';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import {Box, Button, Divider, Grid, Skeleton, Typography} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PropTypes from 'prop-types';
import FormBuilder from '../../Forms/FormBuilder/FormBuilder';
import {FORM_REQUEST_STATUSES} from '../../../../constants/formRequestStatuses';
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ImageWithConditionalRender from '../../../../shared-components/ImageWithConditionalRender';

const FormRequest = ({
                         publicForm,
                         generalForm,
                         onSubmit,
                         formRequest,
                         isLoading,
                         isSubmitting,
                         error,
                         navigateBackRoute,
                         onDownloadAttachment,
                         formAccessLevel,
                         formRequestId,
                         onEmailCheck
                     }) => {
    const navigate = useNavigate();

    const handleSubmit = (data, placeholders) => {
        onSubmit(data, placeholders);
    };
    const schoolLogoComponent = formRequest?.schema?.find(x => x.type === 'schoolLogo');

    const Header = () =>
        <Box
            sx={{
                my: 1,
                px: 3,
                py: 6,
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#ffffff'
            }}
        >
            <Typography variant="h4" component="h1" gutterBottom>
                {isLoading ? <Skeleton variant="rounded"/> : (formRequest?.formName ?? null)}
            </Typography>
            {
                isLoading
                    ? <Skeleton variant="rounded"/>
                    : generalForm || formRequest.isCommunication
                        ? null
                        : <AdmicityStatus {...getFormRequestStatusInfo(formRequest.status)}/>
            }
        </Box>;

    const BackButton = () =>
        <Button
            variant="text"
            startIcon={<ArrowBackRoundedIcon/>}
            onClick={() => navigate(navigateBackRoute)}
        >
            Back
        </Button>;

    const ErrorView = () =>
        error
            ? <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                justifyContent="center"
                alignItems="center"
                minHeight="100%"
                marginTop={1}
                marginBottom={1}
            >
                <ErrorIcon color="error" sx={{
                    fontSize: 80,
                    mb: 2,
                    stroke: 'rgb(250, 250, 251)',
                    strokeWidth: 0.5,
                    opacity: 0.7
                }}/>
                <Typography>
                    {error.message}
                </Typography>
            </Box>
            : '';

    return (
        <Grid container flexGrow={1} minWidth={'100%'} maxHeight={'100%'}>
            <Grid container item xs={12} overflow="auto" justifyContent="center">
                {
                    publicForm
                        ? <Grid item xs={12} sm={10} md={8} lg={6}>
                            {
                                !isLoading && error
                                    ? <ErrorView/>
                                    : <>
                                        {schoolLogoComponent?.url && (
                                            <Box
                                                textAlign="center"
                                                sx={{
                                                    mx: {
                                                        xs: 2,
                                                        sm: 0
                                                    },
                                                    mt: 2,
                                                    mb: 1,
                                                    px: 3,
                                                    py: 6,
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                }}
                                            >
                                                <ImageWithConditionalRender
                                                    imageUrl={schoolLogoComponent.url}
                                                    alt="School logo"
                                                />
                                            </Box>
                                        )}
                                        <FormContent
                                            formRequest={formRequest}
                                            handleSubmit={handleSubmit}
                                            isLoading={isLoading}
                                            isSubmitting={isSubmitting}
                                            onDownloadAttachment={onDownloadAttachment}
                                            generalForm={generalForm}
                                            publicForm={publicForm}
                                            formAccessLevel={formAccessLevel}
                                            formRequestId={formRequestId}
                                            onEmailCheck={onEmailCheck}
                                        />
                                    </>
                            }
                        </Grid>
                        : <Grid item xs={12} sm={10} md={8} lg={6}>
                            <Box sx={{mb: 1}}>
                                <BackButton/>
                                <Divider sx={{mb: 2}}/>
                            </Box>

                            {
                                !isLoading && error
                                    ? <ErrorView/>
                                    : <>
                                        {schoolLogoComponent?.url && (
                                            <Box
                                                textAlign="center"
                                                sx={{
                                                    mb: 2,
                                                    px: 3,
                                                    py: 6,
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#ffffff',
                                                }}
                                            >
                                                <ImageWithConditionalRender
                                                    imageUrl={schoolLogoComponent.url}
                                                    alt="School logo"
                                                />
                                            </Box>
                                        )}
                                        <Header/>
                                        <FormContent
                                            formRequest={formRequest}
                                            handleSubmit={handleSubmit}
                                            isLoading={isLoading}
                                            isSubmitting={isSubmitting}
                                            onDownloadAttachment={onDownloadAttachment}
                                            generalForm={generalForm}
                                            publicForm={publicForm}
                                            formAccessLevel={formAccessLevel}
                                            formRequestId={formRequestId}
                                            onEmailCheck={onEmailCheck}
                                        />
                                    </>
                            }
                        </Grid>
                }
            </Grid>
        </Grid>
    );
};

FormRequest.propTypes = {
    publicForm: PropTypes.bool,
    generalForm: PropTypes.bool,
    onSubmit: PropTypes.func,
    onDownloadAttachment: PropTypes.func,
    formRequest: PropTypes.object,
    isLoading: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    error: PropTypes.object,
    navigateBackRoute: PropTypes.string.isRequired,
    formAccessLevel: PropTypes.number,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onEmailCheck: PropTypes.func
};

FormRequest.defaultProps = {
    publicForm: false,
    generalForm: false,
    isLoading: false,
    isSubmitting: false,
};

function FormContent({
                         isLoading,
                         isSubmitting,
                         formRequest,
                         handleSubmit,
                         onDownloadAttachment,
                         publicForm,
                         generalForm,
                         formAccessLevel,
                         formRequestId,
                         onEmailCheck
                     }) {
    if (isLoading) {
        return (
            <Box sx={{
                display: 'grid',
                gap: 2
            }}>
                {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={120}/>
                ))}
                <Skeleton variant="rounded" width={120} height={36} sx={{ml: 'auto'}}/>
            </Box>
        );
    }

    if (!formRequest?.schema) return null;

    const isParentContactForm =
        Array.isArray(formRequest?.schema) &&
        formRequest.schema.some(c => c.key === 'parentEmail');

    const readOnly =
        !publicForm &&
        !generalForm &&
        (formRequest.status === undefined ||
            formRequest.status === FORM_REQUEST_STATUSES.submitted ||
            formRequest.status !== FORM_REQUEST_STATUSES.sent);

    return (
        <Box>
            <FormBuilder
                schema={formRequest.schema}
                placeholderValues={formRequest.placeholderValues}
                submitMode={true}
                handleSubmit={handleSubmit}
                handleDownloadAttachment={onDownloadAttachment}
                data={formRequest.data}
                readOnly={readOnly || formRequest.isCommunication}
                publicForm={publicForm}
                isCommunication={formRequest.isCommunication}
                accessLevel={formAccessLevel}
                formRequestId={formRequestId}
                onEmailCheck={
                    isParentContactForm
                        ? onEmailCheck
                        : undefined
                }
            />
            <Backdrop open={isSubmitting} sx={{
                color: '#fff',
                zIndex: theme => theme.zIndex.modal + 1
            }}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
}

FormContent.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    formRequest: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onDownloadAttachment: PropTypes.func,
    publicForm: PropTypes.bool,
    generalForm: PropTypes.bool,
    formAccessLevel: PropTypes.number,
    formRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tag: PropTypes.string,
    onEmailCheck: PropTypes.func
};

FormContent.defaultProps = {
    onDownloadAttachment: () => {
    },
    publicForm: false,
    generalForm: false
};

export default FormRequest;
